import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Users, UserPlus, Loader2, Mail, Clock, CheckCircle2 } from "lucide-react";

const PERMS = [
  { key: "roadmap", label: "View roadmap" }, { key: "meetings", label: "View meetings & recaps" },
  { key: "documents", label: "View documents" }, { key: "checklist", label: "View checklist" },
  { key: "messages", label: "View & send messages" }, { key: "referrals", label: "View referrals" },
  { key: "upload", label: "Upload documents" }, { key: "complete_checklist", label: "Complete checklist items" },
];

export default function FamilyAccessCard() {
  const [data, setData] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", relationship_to_loved_one: "" });
  const [perms, setPerms] = useState({ roadmap: true, meetings: true, documents: true, checklist: true, messages: true, referrals: true, upload: false, complete_checklist: false });

  const load = () => api.get("/portal/family-members").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email.trim()) { toast.error("Please enter an email."); return; }
    setInviting(true);
    try { await api.post("/portal/family-members/invite", { ...form, permissions: perms, base_url: window.location.origin }); toast.success("Invitation sent."); setForm({ email: "", full_name: "", relationship_to_loved_one: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not send invite.")); }
    finally { setInviting(false); }
  };

  if (!data || !data.is_primary) return null;  // only the primary member can manage family access

  return (
    <Card className="p-6 border-[#E6EDF2] mt-6" data-testid="family-access-card">
      <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358] mb-1"><Users size={18} /> Family access</div>
      <p className="text-[14px] text-muted-foreground mb-4">Invite another family member to help. You choose exactly what they can see and do.</p>

      {(data.items || []).length > 0 && (
        <div className="space-y-2 mb-4">
          {data.items.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-lg border border-[#EEF2F5] px-3 py-2 text-[14px]" data-testid={`family-member-${m.id}`}>
              {m.status === "pending" ? <Clock size={15} className="text-[#B7791F]" /> : <CheckCircle2 size={15} className="text-[#2F7D5A]" />}
              <span className="font-medium text-[#252221]">{m.full_name}</span>
              <span className="text-[#9AA5B1] flex items-center gap-1"><Mail size={12} /> {m.email}</span>
              <span className="ml-auto text-[12px] capitalize text-[#6B7280]">{m.status}</span>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl bg-[#F4F8FB] p-4 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-[13px] text-[#4B5563]">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="family@email.com" className="mt-1 h-10 text-[14px]" data-testid="family-invite-email" /></div>
          <div><Label className="text-[13px] text-[#4B5563]">Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 h-10 text-[14px]" data-testid="family-invite-name" /></div>
          <div><Label className="text-[13px] text-[#4B5563]">Relationship</Label><Input value={form.relationship_to_loved_one} onChange={(e) => setForm({ ...form, relationship_to_loved_one: e.target.value })} placeholder="e.g. Son" className="mt-1 h-10 text-[14px]" data-testid="family-invite-relationship" /></div>
        </div>
        <div>
          <Label className="text-[13px] text-[#4B5563]">What can they do?</Label>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-1.5">
            {PERMS.map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-2">
                <span className="text-[13px] text-[#252221]">{p.label}</span>
                <Switch checked={!!perms[p.key]} onCheckedChange={(v) => setPerms({ ...perms, [p.key]: v })} data-testid={`family-perm-${p.key}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={invite} disabled={inviting} className="bg-[#1C4358] hover:bg-[#163545] text-white gap-1.5 h-10 rounded-full px-5" data-testid="family-invite-btn">
            {inviting ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Send invite
          </Button>
        </div>
      </div>
    </Card>
  );
}
